import { useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { errorMessage } from "@/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";

/** The minimal shape a deletable Build entity needs for the confirm dialog. */
export interface DeletableEntity {
  uuid: string;
  name: string;
}

/**
 * Delete-with-confirm for a Build entity, shared across all six. Owns the
 * target state, the mutation, list invalidation, and the success/error toast;
 * renders the confirm Modal. Returns a trigger (`onDelete(entity)`) and the
 * `dialog` element to render.
 *
 * The per-entity seams are the mutation, its list query key, the path-param
 * builder (each endpoint names it differently — `agent_uuid`, `flow_uuid`…),
 * and the toast messages (passed resolved, because the typed i18n keys are
 * per-namespace literals). Entities with extra confirm content (e.g. a skill's
 * delete-impact) pass `renderExtra`.
 */
export function useDeleteEntity<T extends DeletableEntity>({
  mutation,
  listQueryKey,
  pathFor,
  messages,
  onDeleted,
  renderExtra,
}: Readonly<{
  /** The generated TanStack mutation options (e.g. `deleteBrandMutation()`). */
  // biome-ignore lint/suspicious/noExplicitAny: the generated mutation options carry entity-specific types; the hook only spreads them and calls mutate.
  mutation: any;
  listQueryKey: () => readonly unknown[];
  /** Build the mutation `path` from the target entity. */
  pathFor: (entity: T) => Record<string, string>;
  /** Resolved i18n strings (the caller's namespace is a typed literal). */
  messages: { deleted: string; deleteFailed: string };
  onDeleted?: () => void;
  /** Optional extra body content in the confirm dialog (e.g. delete-impact). */
  renderExtra?: (entity: T) => ReactNode;
}>) {
  const { t } = useTranslation("build");
  const toast = useToast();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<T | null>(null);

  const remove = useMutation({
    ...mutation,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: listQueryKey() });
      toast.success(messages.deleted);
      setTarget(null);
      onDeleted?.();
    },
    onError: (error: unknown) => toast.error(errorMessage(error, messages.deleteFailed)),
  }) as { mutate: (vars: { path: Record<string, string> }) => void; isPending: boolean };

  const dialog = (
    <ConfirmDialog
      open={target !== null}
      onClose={() => setTarget(null)}
      title={target ? t("delete.confirmTitle", { name: target.name }) : undefined}
      body={t("delete.confirmBody")}
      confirmLabel={t("delete.confirmAction")}
      loading={remove.isPending}
      onConfirm={() => target && remove.mutate({ path: pathFor(target) })}
    >
      {target && renderExtra?.(target)}
    </ConfirmDialog>
  );

  return { onDelete: setTarget, dialog };
}
